/**
 * Utility functions for managing purchase flow data in sessionStorage
 */

const STORAGE_KEY = "networkPlanPurchaseFlow";

export interface NetworkPlanData {
  activeTab?: string;
  networkTab?: string;
  planLevel?: number;
  selectedLines?: number;
  // Single Network progress bar values
  singleNetworkPlanLevel?: number;
  singleNetworkSecurityProtection?: number;
  // Multi Network progress bar values
  multiNetworkPlanLevel?: number;
  multiNetworkSecurityProtection?: number;
  multiNetworkLines?: number;
  multiNetworkPrimaryData?: number;
  coverageDialogSeen?: boolean;
}

export interface SingleNetworkSelectionData {
  makeASelection?: {
    selection?: number;
    email?: string;
    password?: string;
  };
  deviceCompatibility?: {
    brand?: string;
    model?: string;
    simType?: string;
    simNumber?: string;
    imeiNumber?: string;
    simVerified?: boolean;
    imeiVerified?: boolean;
  };
  typeOfNumber?: {
    selectedLabel?: number;
    mdn?: string;
  };
}

export interface MultiNetworkSelectionData {
  selectionAndCoverage?: {
    selection?: number;
    email?: string;
    password?: string;
  };
  phoneType?: {
    phones?: Array<{
      brand: string;
      model: string;
      simType: string;
    }>;
    simNumbers?: Record<string, string>;
    imeiNumbers?: Record<string, string>;
  };
  numberType?: Array<{
    selectedLabel: number | null;
    mdn: string;
    isValidated: boolean;
  }>;
  numberInformation?: {
    transferInfo?: {
      firstName: string;
      lastName: string;
      address: string;
      apt: string;
      city: string;
      zipCode: string;
      state: string;
      accountNumber: string;
      transferPin: string;
      numberToTransfer: string;
    };
    newNumberInfo?: {
      firstName: string;
      lastName: string;
      address: string;
      apt: string;
      city: string;
      zipCode: string;
      state: string;
    };
  };
}

export interface CheckoutData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  securityQuestion?: string;
  answer?: string;
  streetAddress?: string;
  apt?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  source?: string;
  nameOnCredit?: string;
  cardNumber?: boolean;
  cardExpiry?: boolean;
  cardCvc?: boolean;
  acceptTerms?: boolean;
  trialOption?: "skip" | "subscribe";
  promoCode?: string;
  paymentMethodId?: string;
  selectedPaymentMethod?: string;
  selectedPaymentMethodType?: string;
}

export interface ReduxStateData {
  planData?: any;
  checkAvailabilityDetails?: any;
  lines?: number;
  compatibilityData?: any;
  compatibilitiesData?: any[];
  isPorting?: boolean;
  portRequestSubmitted?: boolean;
}

export interface OrderSummaryData {
  regularOrderSummaryData?: any;
  topupOrderSummaryData?: any;
  digitalSecurityData?: any;
  upgradeOrderSummaryData?: any;
  securityPlansOrderSummaryData?: any;
  orderSummaryParams?: any;
  topupOrderSummaryParams?: any;
  digitalSecurityOrderSummaryParams?: any;
  securityPlansOrderSummaryParams?: any;
  appliedCoupon?: string;
  isCouponApplied?: boolean;
}

export interface AddNewLineCheckoutData {
  getNewLineData?: any;
  orderSummaryParams?: {
    multiNetworkPlanId?: string;
    plan_id?: string;
    promoCode?: string;
    securityPlanId?: string;
    zip_code?: string;
  };
  deviceData?: any[];
  compatibilitiesData?: any[];
  planDetails?: any;
  customerData?: any;
  from?: string;
  // Intermediate state for add-new-line flow
  line?: number;
  currentStep?:
    | "add-line"
    | "device-selection"
    | "vle-sim-dialog"
    | "number-type"
    | "upgrade-plan"
    | "checkout";
  simCardData?: Array<{
    simNumber: string;
    imeiNumber: string;
  }>;
  numberTypeData?: Array<{
    selectedLabel: number | null;
    mdn?: string;
    isValidated?: boolean;
  }>;
  hasVLESim?: boolean | null;
  showNewLineData?: boolean;
  hasCalledNewLine?: boolean;
  simVerifications?: boolean[];
  imeiVerifications?: boolean[];
  phoneValidationLoading?: boolean[];
  phoneValidationErrors?: boolean[];
  deviceCompatData?: any[];
  // Selection data for "I have an account" flow
  selectionAndCoverage?: {
    selection?: number;
    email?: string;
    password?: string;
  };
  // Order summary API response (payment-details API payload)
  orderSummaryData?: any;
}

export interface PurchaseFlowData {
  networkPlan?: NetworkPlanData;
  singleNetworkSelection?: SingleNetworkSelectionData;
  multiNetworkSelection?: MultiNetworkSelectionData;
  checkout?: CheckoutData;
  reduxState?: ReduxStateData;
  orderSummary?: OrderSummaryData;
  addNewLineCheckout?: AddNewLineCheckoutData;
}

/**
 * Get all purchase flow data from sessionStorage
 */
export const getPurchaseFlowData = (): PurchaseFlowData | null => {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(
      "Error reading purchase flow data from sessionStorage:",
      error
    );
    return null;
  }
};

/**
 * Save purchase flow data to sessionStorage
 */
export const savePurchaseFlowData = (data: PurchaseFlowData): void => {
  try {
    const existing = getPurchaseFlowData() || {};
    const updated = { ...existing, ...data };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving purchase flow data to sessionStorage:", error);
  }
};

/**
 * Save specific section of purchase flow data.
 * Merges into the existing network plan so partial updates never drop unrelated fields.
 * Patch keys with value `undefined` are skipped so JSON serialization does not strip
 * previously stored values (e.g. Individual = 0 on singleNetworkSecurityProtection).
 */
export const saveNetworkPlanData = (data: NetworkPlanData): void => {
  try {
    const existing = getPurchaseFlowData() || {};
    const prevPlan = existing.networkPlan || {};
    const patch = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    ) as NetworkPlanData;
    savePurchaseFlowData({
      networkPlan: { ...prevPlan, ...patch },
    });
  } catch (error) {
    console.error("Error saving network plan data:", error);
  }
};

export const saveSingleNetworkSelectionData = (
  data: SingleNetworkSelectionData
): void => {
  const existing = getPurchaseFlowData() || {};
  const existingSingle = existing.singleNetworkSelection || {};
  savePurchaseFlowData({
    singleNetworkSelection: { ...existingSingle, ...data },
  });
};

export const saveMultiNetworkSelectionData = (
  data: MultiNetworkSelectionData
): void => {
  const existing = getPurchaseFlowData() || {};
  const existingMulti = existing.multiNetworkSelection || {};
  savePurchaseFlowData({
    multiNetworkSelection: { ...existingMulti, ...data },
  });
};

export const saveCheckoutData = (data: CheckoutData): void => {
  savePurchaseFlowData({ checkout: data });
};

/**
 * Get specific section of purchase flow data
 */
export const getNetworkPlanData = (): NetworkPlanData | null => {
  const data = getPurchaseFlowData();
  return data?.networkPlan || null;
};

export const getSingleNetworkSelectionData =
  (): SingleNetworkSelectionData | null => {
    const data = getPurchaseFlowData();
    return data?.singleNetworkSelection || null;
  };

export const getMultiNetworkSelectionData =
  (): MultiNetworkSelectionData | null => {
    const data = getPurchaseFlowData();
    return data?.multiNetworkSelection || null;
  };

export const getCheckoutData = (): CheckoutData | null => {
  const data = getPurchaseFlowData();
  return data?.checkout || null;
};

/**
 * Clear all purchase flow data from sessionStorage
 */
export const clearPurchaseFlowData = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error(
      "Error clearing purchase flow data from sessionStorage:",
      error
    );
  }
};

/**
 * Clear specific section of purchase flow data
 */
export const clearNetworkPlanData = (): void => {
  const data = getPurchaseFlowData();
  if (data) {
    delete data.networkPlan;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
};

export const clearSingleNetworkSelectionData = (): void => {
  const data = getPurchaseFlowData();
  if (data) {
    delete data.singleNetworkSelection;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
};

export const clearMultiNetworkSelectionData = (): void => {
  const data = getPurchaseFlowData();
  if (data) {
    delete data.multiNetworkSelection;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
};

export const clearCheckoutData = (): void => {
  const data = getPurchaseFlowData();
  if (data) {
    delete data.checkout;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
};

/**
 * Save Redux state data to sessionStorage
 */
export const saveReduxStateData = (data: ReduxStateData): void => {
  const existing = getPurchaseFlowData() || {};
  const existingRedux = existing.reduxState || {};
  savePurchaseFlowData({
    reduxState: { ...existingRedux, ...data },
  });
};

/**
 * Get Redux state data from sessionStorage
 */
export const getReduxStateData = (): ReduxStateData | null => {
  const data = getPurchaseFlowData();
  return data?.reduxState || null;
};

/**
 * Clear Redux state data from sessionStorage
 */
export const clearReduxStateData = (): void => {
  const data = getPurchaseFlowData();
  if (data) {
    delete data.reduxState;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
};

/**
 * Save order summary data to sessionStorage
 */
export const saveOrderSummaryData = (data: OrderSummaryData): void => {
  const existing = getPurchaseFlowData() || {};
  const existingOrderSummary = existing.orderSummary || {};
  savePurchaseFlowData({
    orderSummary: { ...existingOrderSummary, ...data },
  });
};

/**
 * Get order summary data from sessionStorage
 */
export const getOrderSummaryData = (): OrderSummaryData | null => {
  const data = getPurchaseFlowData();
  return data?.orderSummary || null;
};

/**
 * Clear order summary data from sessionStorage
 */
export const clearOrderSummaryData = (): void => {
  const data = getPurchaseFlowData();
  if (data) {
    delete data.orderSummary;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
};

/**
 * Save add-new-line checkout data to sessionStorage
 */
export const saveAddNewLineCheckoutData = (
  data: AddNewLineCheckoutData
): void => {
  const existing = getPurchaseFlowData() || {};
  const existingAddNewLine = existing.addNewLineCheckout || {};
  savePurchaseFlowData({
    addNewLineCheckout: { ...existingAddNewLine, ...data },
  });
};

/**
 * Get add-new-line checkout data from sessionStorage
 */
export const getAddNewLineCheckoutData = (): AddNewLineCheckoutData | null => {
  const data = getPurchaseFlowData();
  return data?.addNewLineCheckout || null;
};

/**
 * Clear add-new-line checkout data from sessionStorage
 */
export const clearAddNewLineCheckoutData = (): void => {
  const data = getPurchaseFlowData();
  if (data) {
    delete data.addNewLineCheckout;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
};
